import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  AutocompleteMultiSelect,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from '@shigo-ui/ui';

import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { useReportShareUserOptions, useSavedReportMutations } from '../../hooks';
import {
  ReportDeliveryFrequency,
  savedReportSharedUserIds,
  type SavedReportConfig,
  type ScheduledReportConfig,
} from '../../model';
import { parseReportRecipients } from './reportRecipients';

const formSchema = z.object({
  name: z.string().trim().min(1),
  sharedWith: z.array(z.string()),
  recipients: z.string().refine((value) => parseReportRecipients(value).length > 0, {
    error: () => dashboardT('savedReports.schedule.recipientsRequired'),
  }),
  deliveryFrequency: z.enum(ReportDeliveryFrequency, {
    error: () => dashboardT('savedReports.schedule.frequencyRequired'),
  }),
});

type ScheduleReportFormValue = z.infer<typeof formSchema>;

interface ScheduleReportDialogProps {
  agencyId?: string | undefined;
  report: SavedReportConfig;
  schedule: ScheduledReportConfig | null;
  onClose: () => void;
}

export function ScheduleReportDialog({
  report,
  schedule,
  onClose,
  agencyId,
}: ScheduleReportDialogProps) {
  'use no memo';

  const mutations = useSavedReportMutations(agencyId);
  const users = useReportShareUserOptions(Boolean(schedule), agencyId);
  const [metadataSaved, setMetadataSaved] = useState(false);
  const editing = Boolean(schedule);
  const pending =
    mutations.createSchedule.isPending ||
    mutations.update.isPending ||
    mutations.updateSchedule.isPending;
  const guard = useBusyDialogGuard(pending, onClose);
  const form = useForm<ScheduleReportFormValue>({
    defaultValues: {
      name: report.name,
      sharedWith: savedReportSharedUserIds(report),
      recipients: schedule?.recipients.join('\n') ?? '',
      deliveryFrequency: schedule?.deliveryFrequency ?? ReportDeliveryFrequency.Daily,
    },
    resolver: zodResolver(formSchema),
  });
  const rootError = form.formState.errors.root?.message;

  async function submit(values: ScheduleReportFormValue) {
    form.clearErrors('root');
    const schedulePayload = {
      recipients: parseReportRecipients(values.recipients),
      deliveryFrequency: values.deliveryFrequency,
    };
    try {
      if (!schedule) {
        await mutations.createSchedule.mutateAsync({
          reportId: report.id,
          payload: schedulePayload,
        });
        toast.success(dashboardT('savedReports.schedule.created'));
        onClose();
        return;
      }
      if (!metadataSaved) {
        await mutations.update.mutateAsync({
          id: report.id,
          payload: {
            name: values.name,
            sharedWith: values.sharedWith,
            report: report.report,
            chartReport: report.chartReport,
            settings: report.settings,
          },
        });
        setMetadataSaved(true);
      }
      try {
        await mutations.updateSchedule.mutateAsync({
          scheduleId: schedule.id,
          payload: schedulePayload,
        });
      } catch (error) {
        showRequestError(error, dashboardT('savedReports.schedule.partialError'));
        form.setError('root', {
          message: dashboardT('savedReports.schedule.partialError'),
        });
        return;
      }
      toast.success(dashboardT('savedReports.schedule.updated'));
      onClose();
    } catch (error) {
      showRequestError(error, dashboardT('savedReports.schedule.error'));
      form.setError('root', { message: dashboardT('savedReports.schedule.error') });
    }
  }

  const scheduleFields = (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="schedule-recipients" className="text-[13px] font-semibold">
          {dashboardT('savedReports.schedule.recipients')}{' '}
          <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="schedule-recipients"
          rows={5}
          disabled={pending}
          aria-invalid={Boolean(form.formState.errors.recipients)}
          {...form.register('recipients')}
        />
        <p className="text-muted-foreground text-xs">
          {dashboardT('savedReports.schedule.recipientsHint')}
        </p>
        {form.formState.errors.recipients?.message ? (
          <p className="text-destructive text-xs" role="alert">
            {form.formState.errors.recipients.message}
          </p>
        ) : null}
      </div>
      <div className="space-y-1.5">
        <Label className="text-[13px] font-semibold">
          {dashboardT('savedReports.schedule.frequency')}{' '}
          <span className="text-destructive">*</span>
        </Label>
        <Controller
          control={form.control}
          name="deliveryFrequency"
          render={({ field }) => (
            <Select value={field.value} disabled={pending} onValueChange={field.onChange}>
              <SelectTrigger aria-invalid={Boolean(form.formState.errors.deliveryFrequency)}>
                <SelectValue placeholder={dashboardT('savedReports.schedule.frequency')} />
              </SelectTrigger>
              <SelectContent>
                {Object.values(ReportDeliveryFrequency).map((frequency) => (
                  <SelectItem key={frequency} value={frequency}>
                    {frequencyMessage(frequency)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {form.formState.errors.deliveryFrequency?.message ? (
          <p className="text-destructive text-xs" role="alert">
            {form.formState.errors.deliveryFrequency.message}
          </p>
        ) : null}
      </div>
    </div>
  );

  return (
    <Dialog open onOpenChange={guard.onOpenChange}>
      <DialogContent
        className="max-w-lg gap-0 p-0"
        onEscapeKeyDown={guard.onDismissInteraction}
        onPointerDownOutside={guard.onDismissInteraction}
      >
        <DialogHeader className="border-border border-b px-5 py-4">
          <DialogTitle className="text-base">
            {dashboardT(
              editing ? 'savedReports.schedule.editTitle' : 'savedReports.schedule.title',
            )}
          </DialogTitle>
          <DialogDescription>{report.name}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={(event) => void form.handleSubmit(submit)(event)} noValidate>
            {editing ? (
              <Tabs defaultValue="edit">
                <TabsList className="mx-5 mt-4 grid w-[calc(100%-2.5rem)] grid-cols-2">
                  <TabsTrigger value="edit">
                    {dashboardT('savedReports.schedule.editShare')}
                  </TabsTrigger>
                  <TabsTrigger value="schedule">
                    {dashboardT('savedReports.schedule.tab')}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="edit" className="space-y-5 px-5 py-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="scheduled-report-name" className="text-[13px] font-semibold">
                      {dashboardT('reportExplorer.save.name')}{' '}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="scheduled-report-name"
                      disabled={pending || metadataSaved}
                      {...form.register('name')}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[13px] font-semibold">
                      {dashboardT('reportExplorer.save.sharedWith')}
                    </Label>
                    <Controller
                      control={form.control}
                      name="sharedWith"
                      render={({ field }) => (
                        <AutocompleteMultiSelect
                          label={dashboardT('reportExplorer.save.sharedWith')}
                          options={users.data ?? []}
                          value={field.value}
                          loading={users.isFetching}
                          disabled={pending || metadataSaved}
                          placeholder={dashboardT('reportExplorer.save.searchUsers')}
                          emptyMessage={dashboardT('reportExplorer.save.noUsers')}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </TabsContent>
                <TabsContent value="schedule" className="px-5 py-5">
                  {scheduleFields}
                </TabsContent>
              </Tabs>
            ) : (
              <div className="px-5 py-5">{scheduleFields}</div>
            )}
            {rootError ? (
              <p className="text-destructive px-5 pb-3 text-xs" role="alert">
                {rootError}
              </p>
            ) : null}
            <DialogFooter className="border-border border-t px-5 py-3">
              <Button type="button" variant="outline" disabled={pending} onClick={guard.close}>
                {dashboardT('savedReports.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {dashboardT('savedReports.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function frequencyMessage(frequency: ReportDeliveryFrequency) {
  if (frequency === ReportDeliveryFrequency.Daily) {
    return dashboardT('savedReports.schedule.daily');
  }
  if (frequency === ReportDeliveryFrequency.Weekly) {
    return dashboardT('savedReports.schedule.weekly');
  }
  return dashboardT('savedReports.schedule.monthly');
}
